import { Button } from "@/components/ui/button";
import { ArrowRight, Home, Ruler, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import CanonicalTag from "@/components/CanonicalTag";

const ModernADUDesignSanCarlos = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <CanonicalTag url="https://udaadu.com/modern-adu-design-san-carlos" />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-6">
            Modern ADU Design in San Carlos
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Transform your San Carlos property with sleek, contemporary ADU designs that maximize space efficiency while embracing clean lines and innovative materials.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="group">
              Get Your Modern ADU Quote
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" size="lg">
              View Modern Portfolio
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-muted/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Modern ADU Design in San Carlos?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-lg bg-card border">
              <Home className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Clean Aesthetics</h3>
              <p className="text-muted-foreground">
                Minimalist design principles that create spacious, uncluttered living environments perfect for San Carlos' refined neighborhoods.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-card border">
              <Ruler className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Space Optimization</h3>
              <p className="text-muted-foreground">
                Smart layouts and built-in storage solutions that maximize every square foot while maintaining the open feel modern design demands.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-card border">
              <Zap className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Energy Efficiency</h3>
              <p className="text-muted-foreground">
                Contemporary materials and systems that reduce environmental impact while lowering utility costs for your San Carlos ADU.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto prose prose-lg">
          <h2 className="text-3xl font-bold mb-6">Modern ADU Design Excellence in San Carlos</h2>
          
          <p className="text-lg text-muted-foreground mb-6">
            San Carlos homeowners seeking cutting-edge ADU designs benefit from modern architectural principles that emphasize functionality, sustainability, and visual appeal. Our modern ADU designs feature floor-to-ceiling windows, open-concept layouts, and premium finishes that complement San Carlos' diverse architectural landscape.
          </p>

          <h3 className="text-2xl font-semibold mb-4">Key Modern Design Elements</h3>
          <ul className="space-y-2 text-muted-foreground mb-6">
            <li>• Large windows and glass doors for natural light</li>
            <li>• Flat or shed roofs with clean lines</li>
            <li>• Mixed materials like steel, glass, and wood</li>
            <li>• Open floor plans with flexible spaces</li>
            <li>• High-end appliances and smart home features</li>
            <li>• Sustainable building practices and materials</li>
          </ul>

          <h3 className="text-2xl font-semibold mb-4">San Carlos Modern ADU Benefits</h3>
          <p className="text-muted-foreground mb-6">
            Modern ADUs in San Carlos not only provide additional living space but also significantly increase property values. The contemporary aesthetic appeals to tech professionals and young families drawn to the Peninsula's innovation corridor, making these units highly desirable for both rental income and family use.
          </p>

          <div className="bg-muted/20 p-6 rounded-lg mb-8">
            <h4 className="text-xl font-semibold mb-3">Ready to Build Your Modern ADU in San Carlos?</h4>
            <p className="text-muted-foreground mb-4">
              Our team specializes in creating stunning modern ADUs that comply with San Carlos building codes while exceeding your design expectations.
            </p>
            <Button className="group">
              Start Your Modern ADU Project
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Transform Your San Carlos Property Today</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Join hundreds of satisfied homeowners who've added value and space with our modern ADU designs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="group">
              Get Free Modern ADU Consultation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">Contact Our Design Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ModernADUDesignSanCarlos;